import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { ServiceManagementService } from '../services/service-management.service';
import { ServiceManagementActions } from './service-management.actions';

@Injectable()
export class ServiceManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly serviceManagementService = inject(ServiceManagementService);

  readonly loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.loadList),
      switchMap(({ query }) =>
        forkJoin({
          list: this.serviceManagementService.listServices(query),
          stats: this.serviceManagementService.getServiceStats(),
        }).pipe(
          map(({ list, stats }) =>
            ServiceManagementActions.loadListSuccess({
              rows: list.items,
              total: list.pagination.total,
              page: list.pagination.page,
              pageSize: list.pagination.pageSize,
              stats,
            }),
          ),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.loadListFailure({
                error: getApiErrorMessage(error, 'Unable to load services'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.loadService),
      switchMap(({ serviceId, mode }) =>
        this.serviceManagementService.getService(serviceId).pipe(
          map((service) => ServiceManagementActions.loadServiceSuccess({ service, mode })),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.loadServiceFailure({
                error: getApiErrorMessage(error, 'Unable to load service details'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly createService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.createService),
      switchMap(({ payload }) =>
        this.serviceManagementService.createService(payload).pipe(
          map((response) =>
            ServiceManagementActions.persistSuccess({
              message: response.message,
              service: response.service,
            }),
          ),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.persistFailure({
                error: getApiErrorMessage(error, 'Unable to save service'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.updateService),
      switchMap(({ serviceId, payload }) =>
        this.serviceManagementService.updateService(serviceId, payload).pipe(
          map((response) =>
            ServiceManagementActions.persistSuccess({
              message: response.message,
              service: response.service,
            }),
          ),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.persistFailure({
                error: getApiErrorMessage(error, 'Unable to save service'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateServiceStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.updateServiceStatus),
      switchMap(({ serviceId, payload }) =>
        this.serviceManagementService.updateServiceStatus(serviceId, payload).pipe(
          map((response) =>
            ServiceManagementActions.updateServiceStatusSuccess({
              message: response.message,
              service: response.service,
            }),
          ),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.updateServiceStatusFailure({
                error: getApiErrorMessage(error, 'Unable to update service status'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateServiceSla$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.updateServiceSla),
      switchMap(({ serviceId, payload }) =>
        this.serviceManagementService.updateServiceSla(serviceId, payload).pipe(
          map((response) =>
            ServiceManagementActions.updateServiceSlaSuccess({
              message: response.message,
              service: response.service,
            }),
          ),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.updateServiceSlaFailure({
                error: getApiErrorMessage(error, 'Unable to update service SLA'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly exportServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceManagementActions.exportServices),
      switchMap(({ query }) =>
        this.serviceManagementService.exportServices(query).pipe(
          map((response) => ServiceManagementActions.exportServicesSuccess({ response })),
          catchError((error: unknown) =>
            of(
              ServiceManagementActions.exportServicesFailure({
                error: getApiErrorMessage(error, 'Unable to export services'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly exportServicesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ServiceManagementActions.exportServicesSuccess),
        tap(({ response }) => {
          const blob = new Blob([response.content], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = response.fileName;
          anchor.click();
          URL.revokeObjectURL(url);
        }),
      ),
    { dispatch: false },
  );
}
